import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Checkbox,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../../Redux/AlertReducer";
import { useDispatch } from "react-redux";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ability, createAbility, updateAbility } from "../../../config/acl/ability";

const AddEmployeeForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    photo: null,
    active: 1,
    password: "",
    password_confirmation: "",
    permissions: [],
    manager_id: null,
  });

  const [permissions, setPermissions] = useState([]);

  // Fetch roles from backend
  const getRoles = async () => {
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Roles.GetAll}?per_page=100`,
        hasToken: true,
      });
      if (response && response.data) {
        setRoles(response.data.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Fetch managers
  const getManagers = async () => {
    try {
      const { result, response } = await Helper.Get({
        url: `${api_Routes.Employees.GetManagers}`,
      });
      if (response) {
        setManagers(response.data);
      }
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  // Filter roles based on user permissions
  useEffect(() => {
    if (roles && roles.length > 0) {
      const filtered = roles.filter(role => {
        // Adjust this logic based on your role structure and permissions
        if (ability.can('owner', 'show')) {
          return true; // Owner can see all roles
        } else if (ability.can('admin', 'show')) {
          return role.name !== 'owner'; // Admin can see all except owner
        }
        // Add more permission checks as needed
        return role.name !== 'owner' && role.name !== 'admin';
      });
      setFilteredRoles(filtered);
    }
  }, [roles]);

  // const getPermissions = async () => {
  //   try {
  //     const { result, response } = await Helper.Get({
  //       url: `${api_Routes.Permissions.View}`,
  //     });
  //     if (response) {
  //       setPermissions(response.data);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching details:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  useEffect(() => {
    getRoles();
    getManagers();
    // getPermissions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        photo: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (errorMessage && name === "password") {
        if (value === formData.password_confirmation) setErrorMessage();
      }
      if (errorMessage && name === "password_confirmation") {
        if (value === formData.password) setErrorMessage();
      }
    }
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (e) => {
    const selectedRole = e.target.value;
    setFormData((prev) => ({
      ...prev,
      role: selectedRole,
      // Reset manager_id if role is changed from assistance to something else
      ...(prev.role === "assistance" && selectedRole !== "assistance" && { manager_id: null })
    }));
  };

  const handleManagerChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      manager_id: e.target.value,
    }));
  };

  // const handlePermissionChange = (e) => {
  //   const { checked, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     permissions: checked
  //       ? [...(prev.permissions || []), value]
  //       : prev.permissions.filter((id) => id !== value), // Use .filter directly
  //   }));
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if passwords match
    if (formData.password !== formData.password_confirmation) {
      setErrorMessage(
          "Passwords do not match! Please ensure both passwords are the same."
      );
      return;
    }

    setIsLoading(true);

    const formDataToSend = new FormData();
    for (let key in formData) {
      if (key === "photo") {
        if (formData.photo && typeof formData.photo !== "string") {
          formDataToSend.append("photo", formData.photo);
        }
      } else if (key === "permissions") {
        formData.permissions.forEach((permissionId) => {
          formDataToSend.append("permissions[]", permissionId);
        });
      } else if (formData[key] !== null) {
        formDataToSend.append(key, formData[key]);
      }
    }

    try {
      const { result, response, message } = await Helper.Post({
        url: api_Routes.Employees.Add,
        data: formDataToSend,
        isFormData: true,
      });

      setIsLoading(false);
      if (response) {
        dispatch(setSuccess(true));
        navigate("/Employees");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoading(false);
    }
  };

  return (
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
          <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              required
              autoComplete="off"
              value={formData.name}
              onChange={handleInputChange}
              variant="outlined"
          />
          <div className="mt-3 mb-1">
            <FormControl className="w-full">
              <InputLabel id="role-select-label">Role *</InputLabel>
              <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={formData.role || ""}
                  required
                  onChange={handleRoleChange}
                  label="Role"
                  sx={{ textAlign: "start" }}
                  disabled={isLoadingRoles}
              >
                <MenuItem value="">
                  <em>Select a Role</em>
                </MenuItem>
                {filteredRoles.map((role) => (
                    <MenuItem key={role.id || role.name} value={role.name || role.id}>
                      {role.name || role.display_name}
                    </MenuItem>
                ))}
              </Select>
              {isLoadingRoles && (
                  <div className="flex items-center justify-center mt-2">
                    <CircularProgress size={20} />
                    <span className="ml-2 text-sm text-gray-500">Loading roles...</span>
                  </div>
              )}
            </FormControl>
          </div>
        </div>

        {/* Manager selection - only show for assistants and make it optional */}
        {formData.role === "assistance" && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="manager-select-label">Manager</InputLabel>
                <Select
                    labelId="manager-select-label"
                    id="manager-select"
                    value={formData.manager_id || ""}
                    onChange={handleManagerChange}
                    label="Manager"
                    sx={{ textAlign: "start" }}
                >
                  <MenuItem value="">
                    <em>Select a Manager (Optional)</em>
                  </MenuItem>
                  {managers.map((manager) => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.name}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
          <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              required
              type="email"
              autoComplete="off"
              value={formData.email}
              onChange={handleInputChange}
              variant="outlined"
          />
          <TextField
              fullWidth
              margin="normal"
              label="Upload Image"
              name="file"
              type="file"
              InputLabelProps={{ shrink: true }}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{
                accept: "image/*",
              }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
          <div className="mt-3 mb-3">
            <FormControl className="w-full mt-4 mb-2" variant="outlined">
              <InputLabel htmlFor="outlined-adornment-password">
                Password
              </InputLabel>
              <OutlinedInput
                  fullWidth
                  id="outlined-adornment-password"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                          aria-label={
                            showPassword
                                ? "hide the password"
                                : "display the password"
                          }
                          onClick={handleClickShowPassword}
                          edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                  name="password"
                  required
                  autoComplete="off"
                  value={formData.password}
                  onChange={handleInputChange}
                  variant="outlined"
              />
            </FormControl>
          </div>

          <div className="mt-4 mb-3">
            <FormControl className="w-full" variant="outlined">
              <InputLabel htmlFor="outlined-adornment-confirm-password">
                Confirm password
              </InputLabel>
              <OutlinedInput
                  fullWidth
                  margin="normal"
                  id="outlined-adornment-confirm-password"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                          aria-label={
                            showPassword
                                ? "hide the password"
                                : "display the password"
                          }
                          onClick={handleClickShowPassword}
                          edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm password"
                  name="password_confirmation"
                  required
                  autoComplete="off"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  variant="outlined"
              />
            </FormControl>
          </div>
        </div>
        {errorMessage && (
            <div className="w-full text-start text-red-600">*{errorMessage}</div>
        )}

        <div className="flex items-center justify-between gap-x-3">
          <div className="flex items-center justify-start gap-x-3">
            <FormLabel sx={{ fontWeight: "bold" }} component="legend">
              Active:
            </FormLabel>
            <RadioGroup
                name="active"
                value={formData.active}
                onChange={handleRadioChange}
                row
            >
              <FormControlLabel value="1" control={<Radio />} label="Yes" />
              <FormControlLabel value="0" control={<Radio />} label="No" />
            </RadioGroup>
          </div>
        </div>

        {/*<div className="mt-4">*/}
        {/*  <FormLabel*/}
        {/*    sx={{ fontWeight: "bold", textAlign: "left", width: "100%" }}*/}
        {/*    component="legend"*/}
        {/*  >*/}
        {/*    Permissions:*/}
        {/*  </FormLabel>*/}
        {/*  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">*/}
        {/*    {permissions?.map((permission) => (*/}
        {/*      <FormControlLabel*/}
        {/*        key={permission.id}*/}
        {/*        control={*/}
        {/*          <Checkbox*/}
        {/*            name="permissions[]"*/}
        {/*            value={permission.id.toString()}*/}
        {/*            checked={formData.permissions?.includes(*/}
        {/*              permission.id.toString()*/}
        {/*            )}*/}
        {/*            onChange={handlePermissionChange}*/}
        {/*          />*/}
        {/*        }*/}
        {/*        label={permission.name}*/}
        {/*      />*/}
        {/*    )}*/}
        {/*  </div>*/}
        {/*</div>*/}

        <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading || errorMessage || isLoadingRoles}
            color="primary"
            sx={{
              display: isLoading ? "flex" : "block",
              marginTop: 2,
              width: "20%",
              marginX: "auto",
              fontWeight: "bold",
              justifyContent: "center",
            }}
        >
          {isLoading ? <CircularProgress size={24} color="primary" /> : "Submit"}
        </Button>
      </form>
  );
};

export default AddEmployeeForm;